import { Add } from "@mui/icons-material";
import { Divider, Fab, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";

import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { VerifyResponsibleDialog } from "../../../../components/schedule/VerifyResponsibleDialog";
import { endpoints, oids } from "../../../../configs/Settings";
import { useClinicalRegisterContext } from "../../../../contexts/ClinicalRegisterContext";
import { useInterventionsContext } from "../../../../contexts/InterventionsContext";
import { useOrganizationContext } from "../../../../contexts/OrganizationContext";
import { usePractitionerRoleContext } from "../../../../contexts/PractitionerRoleContext";
import { useQuestionnaireContext } from "../../../../contexts/QuestionnaireContext";
import { useSessionContext } from "../../../../contexts/SessionContext";
import {
  BundlePatientSummary,
  PatchAppointmentStatus,
  PostMedicationRequest,
} from "../../../../services/fhir";
import { PatchEncounterStatus } from "../../../../services/fhir/encounter/PatchEncounterStatus";
import { PatchStatusObservation } from "../../../../services/fhir/observation/PatchObservation";
import { PostObservation } from "../../../../services/fhir/observation/PostObservation";
import { PostDocumentReference } from "../../../../services/fhir/patientsummary/documentreference/PostDocumentReference";
import { PostQuestionnaireResponse } from "../../../../services/fhir/patientsummary/questionnaireResponse/PostQuestionnaireResponse";
import { PutQuestionnaireResponse } from "../../../../services/fhir/patientsummary/questionnaireResponse/PutQuestionnaireResponse";
import { PostServiceRequest } from "../../../../services/fhir/patientsummary/serviceRequest/PostServiceRequest";
import { GetRelatedPerson } from "../../../../services/fhir/relatedperson/GetRelatedPerson";
import { DeleteWhereby } from "../../../../services/whereby/DeleteWhereby";
import { useFhirData } from "../../../../utils/useFhirData";
import { isValidSpecialtyText } from "../../../../utils/resolveAttendanceSpecialty";
import { QuickActionsDialog } from "../QuickActionsDialog";
import { ShortCutsViewer } from "../StepArea/components/ShortCutsViewer";
import {
  BuildQuestionnaireAnswers,
  BuildQuestionnaireResponse,
} from "./BuildQuestionnaireResponse";
import { WarningDocumentsDialog } from "./components/WarningDocumentsDialog";
import { PostCarePlan } from "@/services/fhir/careplan/PostCarePlan";
import { PostCIAP2Condition } from "@/services/fhir/patientsummary/condition/PostCIAP2Condition";
import { PatchCarePlanStatus } from "@/services/fhir/careplan/PatchCarePlanStatus";
import { PutCondition } from "@/services/fhir/patientsummary/condition/PutCondition";
import { PostPhysicalFindinds } from "@/services/fhir/observation/PostPhysicalFindinds";
import { useSidebarNavigation } from "@/contexts/SidebarNavigationContext";
import type { QuestionnairePayload } from "src-next/modules/questionnaire-builder/form-builder/types";
import { useQuestionnaireManagement } from "src-next/modules/questionnaire-builder/questionnaire-management/hooks";
import {
  resolveOptionalAppointmentBasedOn,
  useQuestionnaireTaskSend,
} from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import {
  buildBasedOnXmlForDocumentItems,
  getDocBase64Payload,
  isPendingSignatureIntervention,
  shouldPostInterventionDocument,
  shouldUpsertMedication,
  shouldUpsertService,
  waitForDocBase64Keys,
} from "../../utils/footerInterventionPersist";
import { isQuestionnaireSelectableForClinicalUse } from "@/utils/questionnaireClinicalSelection";
import {
  buildClinicalAutomationTriggerInputFromFhirResources,
  triggerPostConsultationAutomation,
} from "@/services/crm/triggerClinicalAutomation";
import { isEvolutionAppointment } from "@/components/schedule/evolution/isEvolutionAppointment";

function transformObjectToList(data: Record<string, any>) {
  const result: Array<{
    section: string;
    key: string;
    status: "normal" | "alterado";
    alterado?: string;
  }> = [];

  if (!data) return;

  Object?.keys(data)?.forEach((section) => {
    Object?.keys(data?.[section])?.forEach((key) => {
      const { value, alterado } = data?.[section]?.[key];
      result.push({
        section,
        text: key,
        status: value,
        ...(alterado && { description: alterado }), // Adiciona 'alterado' apenas se existir
      });
    });
  });

  return result;
}

export function Footer() {
  const methods = useForm();
  const { control } = methods;
  const {
    activeStep,
    openDialog,
    type,
    setOpenDialog,
    setType,
    handleSubmit,
    appointment,
    setActiveStep,
    clearRegisterData,
    registerAreaTab,

    problems,
    sendLuxorHistoryAsQuestionnaireResponse,
    control: controlContext,
    patient,
    finalizeAttendanceRef,
    setIsFinalizingAttendance,
  } = useMedicalCareContext();

  const examsContext = useWatch({
    control: controlContext,
    name: "exams",
  });

  const examsList = transformObjectToList(examsContext);
  const { setIsSidebarExpanded } = useSidebarNavigation();

  const { questionnaireDefualt } = useQuestionnaireContext();

  const { setInProgress } = useClinicalRegisterContext();
  const { selectedInterventions, base64, appliedProtocol } =
    useInterventionsContext();

  // Snapshot sempre atualizado: handlePrescription pode aguardar PDFs gerados
  // após o mount do step 2 (race ao confirmar sem assinar).
  const base64Ref = useRef(base64);
  useEffect(() => {
    base64Ref.current = base64;
  }, [base64]);

  const { userPractitioner, userPractitionerRole, userSpecialties } =
    usePractitionerRoleContext();
  const { organization, organizationId } = useOrganizationContext();

  const { user, access } = useSessionContext();

  const { sendQuestionnaireTasks } = useQuestionnaireTaskSend();

  const { questionnaires: orgQuestionnairesForSendGuard } =
    useQuestionnaireManagement({
      organizationId: organizationId
        ? `${oids.cnpj}-${organizationId}`
        : "",
    });

  const questionnaireByIdForClinicalSend = useMemo(() => {
    const m = new Map<string, QuestionnairePayload>();
    for (const q of orgQuestionnairesForSendGuard) {
      if (q.id) m.set(q.id.trim(), q);
    }
    return m;
  }, [orgQuestionnairesForSendGuard]);

  /** Mesmas convenções de SendQuestionnairesDialog (tasks no workflow FHIR). */
  const practitionerRoleReference = useMemo(() => {
    const username = user?.username;
    if (!access?.organizationId || typeof username !== "string") return "";
    return `PractitionerRole/${access.organizationId}-${oids.cpf}-${username}`;
  }, [access?.organizationId, user?.username]);

  const patientReferenceForQuestionnaireTasks = useMemo(() => {
    return patient?.id ? `Patient/${patient.id}` : null;
  }, [patient?.id]);

  const appointmentReferenceForQuestionnaireTasks = useMemo(
    () => resolveOptionalAppointmentBasedOn(appointment?.id),
    [appointment?.id],
  );
  const { state } = useLocation();
  const [warningSignature, setWarningSignature] = useState(false);
  const navigate = useNavigate();

  const references = state?.encounterReference;

  const hcsDisplay = useFhirData?.findParticipant(
    appointment?.participant,
    "HealthcareService",
  )?.display;
  const roleSpecialtyDisplay =
    userPractitionerRole?.specialty?.[0]?.coding?.[0]?.display;
  // Evolução/PA/consulta sem HCS: especialidade do PractitionerRole em vez de undefined.
  const healthcare = isValidSpecialtyText(hcsDisplay)
    ? hcsDisplay
    : isValidSpecialtyText(roleSpecialtyDisplay)
      ? roleSpecialtyDisplay
      : undefined;

  const servicePratice = userSpecialties?.find(
    (el: any) => el.coding?.[0]?.display === healthcare,
  );
  const crm = userPractitionerRole?.identifier
    ?.find(
      (id: any) =>
        (id.system === "crm" ||
          id.system === "https://fluxmed.cloud/fhir/CodeSystem/CBO-S") &&
        id?.value?.length === 12,
    )
    ?.value?.split("/")?.[1];

  const crmValue = crm?.slice(2);
  const crmUf = crm?.slice(0, 2);
  const docReferenceData = {
    organization: {
      id: organization?.id,
      alias: organization?.alias?.[0],
      name: organization?.name,
    },
    practitioner: {
      id: userPractitioner?.id,
      name: userPractitioner?.name?.[0]?.text,
    },
    servicePratice: {
      display: healthcare || servicePratice?.coding?.[0]?.display || "",
      code: healthcare || servicePratice?.coding?.[0]?.code || "",
      system: "https://fluxmed.com.br/fhir/CodeSystem/CBO",
    },
    time: {
      start: appointment?.start,
      end: appointment?.end,
    },
  };

  const [loading, setLoading] = useState<boolean>(false);
  // Impede submits simultâneos (ver onSubmit, mais abaixo).
  const isSubmittingRef = useRef(false);

  const handlePrescription = async () => {
    const contained = [
      {
        resourceType: "Practitioner",
        id: "practitioner-1",
        identifier: [
          {
            ...(crmUf &&
              crmValue && {
                extension: [
                  {
                    url: "http://ehrrunner.com/fhir/StructureDefinition/BRJurisdicaoOrgaoEmissor-1.0",
                    valueCodeableConcept: {
                      coding: [
                        {
                          system:
                            "http://ehrrunner.com/fhir/CodeSystem/BRUnidadeFederacao-1.0",
                          code: crmUf,
                        },
                      ],
                    },
                  },
                ],
              }),

            type: {
              coding: [
                {
                  system: "http://terminology.hl7.org/CodeSystem/v2-0203",
                  code: "MD",
                },
              ],
            },
            value: crmValue,
            assigner: {
              display: "CONSELHO REGIONAL DE MEDICINA (CRM)",
            },
          },
          {
            type: {
              coding: [
                {
                  system: "http://terminology.hl7.org/CodeSystem/v2-0203",
                  code: "TAX",
                },
              ],
            },
            value: user?.username,
          },
        ],
        name: [
          {
            text: user?.name,
          },
        ],
      },
    ];

    // Upsert (POST novo / PUT se id+refact); DocumentReference para PDFs pendentes.

    // Certificados pendentes de assinatura → sempre DocumentReference no finish
    const notSignCert = selectedInterventions?.certificates?.filter(
      (el: any) => shouldPostInterventionDocument(el),
    );

    // Chaves de PDF: índices alinhados ao DocumentVisualization (!sign / pendente)
    const base64KeysNeeded: string[] = [];
    if (
      selectedInterventions?.medicationSimple?.some(
        (el: any) =>
          el?.type === "medication" && shouldPostInterventionDocument(el),
      )
    ) {
      base64KeysNeeded.push("simple-medication");
    }
    if (
      selectedInterventions?.medicationSimple?.some(
        (el: any) =>
          el?.type === "supplement" && shouldPostInterventionDocument(el),
      )
    ) {
      base64KeysNeeded.push("simple-supplement");
    }
    if (
      selectedInterventions?.medicationSimple?.some(
        (el: any) =>
          el?.type === "herbalMedicine" && shouldPostInterventionDocument(el),
      )
    ) {
      base64KeysNeeded.push("simple-herbalMedicine");
    }
    selectedInterventions?.medicationSpecial
      ?.filter((e: any) => isPendingSignatureIntervention(e))
      ?.forEach((el: any, index: number) => {
        if (shouldPostInterventionDocument(el)) {
          base64KeysNeeded.push(`special-${index}`);
        }
      });
    if (
      selectedInterventions?.service?.some(
        (el: any) =>
          shouldPostInterventionDocument(el) &&
          !["363679005", "108252007"]?.includes(el?.category?.code),
      )
    ) {
      base64KeysNeeded.push("exam");
    }
    if (
      selectedInterventions?.procedure?.some(shouldPostInterventionDocument)
    ) {
      base64KeysNeeded.push("procedure");
    }
    selectedInterventions?.service
      ?.filter(
        (el: any) =>
          isPendingSignatureIntervention(el) &&
          el?.category?.code === "108252007",
      )
      ?.forEach((el: any, index: number) => {
        if (shouldPostInterventionDocument(el)) {
          base64KeysNeeded.push(`exam-${index}-lab`);
        }
      });
    selectedInterventions?.service
      ?.filter(
        (el: any) =>
          isPendingSignatureIntervention(el) &&
          el?.category?.code === "363679005",
      )
      ?.forEach((el: any, index: number) => {
        if (shouldPostInterventionDocument(el)) {
          base64KeysNeeded.push(`exam-${index}-image`);
        }
      });
    notSignCert?.forEach((_: any, index: number) => {
      base64KeysNeeded.push(`certificate-${index}`);
    });

    // Garante step 2 montado (gera PDF) e espera base64 antes dos posts.
    if (base64KeysNeeded.length) {
      setActiveStep(2);
      const readyBase64 = await waitForDocBase64Keys(
        () => base64Ref.current,
        base64KeysNeeded,
      );
      base64Ref.current = readyBase64;

      const missingKeys = base64KeysNeeded.filter(
        (key) => !getDocBase64Payload(readyBase64, key),
      );
      if (missingKeys.length) {
        console.warn(
          "PDF ainda não pronto para DocumentReference:",
          missingKeys,
        );
        toast.warn(
          "Alguns documentos PDF não ficaram prontos a tempo. Medicamentos podem ficar sem documento vinculado — tente assinar na aba Assinaturas ou reemitir no prontuário.",
        );
      }
    }

    const readyBase64List = base64Ref.current;

    const serviceBase64 = getDocBase64Payload(readyBase64List, "exam");
    const procedureBase64 = getDocBase64Payload(readyBase64List, "procedure");

    // Lab/imagem: índice alinhado ao DocumentVisualization (pendente de assinatura)
    const labUnsigned =
      selectedInterventions?.service?.filter(
        (el: any) =>
          isPendingSignatureIntervention(el) &&
          el?.category?.code === "108252007",
      ) || [];
    const imagingUnsigned =
      selectedInterventions?.service?.filter(
        (el: any) =>
          isPendingSignatureIntervention(el) &&
          el?.category?.code === "363679005",
      ) || [];

    await Promise.allSettled(
      labUnsigned.map(async (exams: any, index: number) => {
        if (
          !shouldUpsertService(exams) &&
          !shouldPostInterventionDocument(exams)
        ) {
          return;
        }

        const examsBaseLab64 = getDocBase64Payload(
          readyBase64List,
          `exam-${index}-lab`,
        );
        if (!examsBaseLab64 && shouldPostInterventionDocument(exams)) return;

        try {
          const toPost = shouldUpsertService(exams) ? [exams] : [];
          let serviceResponse: any;
          if (toPost.length) {
            serviceResponse = await PostServiceRequest(
              toPost,
              references,
              contained,
            );
          }

          // PDF pendente → DocumentReference com basedOn (id existente e/ou POST novo)
          if (examsBaseLab64 && shouldPostInterventionDocument(exams)) {
            const auxServiceBasedOn = buildBasedOnXmlForDocumentItems(
              [exams],
              toPost,
              serviceResponse?.entry,
              "ServiceRequest",
            );
            if (auxServiceBasedOn) {
              await PostDocumentReference(
                {
                  ...docReferenceData,
                  base64: examsBaseLab64,
                  codeValue: "SERVICE_REQUEST",
                  codeDisplay: "Solicitação de exame",
                  categoryValue: "service-request",
                  categoryDisplay: "Solicitação",
                },
                references,
                auxServiceBasedOn,
              );
            }
          }
        } catch (err) {
          console.log("Erro ao processar exame laboratorial:", err);
        }
      }),
    );

    await Promise.allSettled(
      imagingUnsigned.map(async (exams: any, index: number) => {
        if (
          !shouldUpsertService(exams) &&
          !shouldPostInterventionDocument(exams)
        ) {
          return;
        }

        const examsBaseImage64 = getDocBase64Payload(
          readyBase64List,
          `exam-${index}-image`,
        );
        if (!examsBaseImage64 && shouldPostInterventionDocument(exams)) return;

        try {
          const toPost = shouldUpsertService(exams) ? [exams] : [];
          let serviceResponse: any;
          if (toPost.length) {
            serviceResponse = await PostServiceRequest(
              toPost,
              references,
              contained,
            );
          }

          if (examsBaseImage64 && shouldPostInterventionDocument(exams)) {
            const auxServiceBasedOn = buildBasedOnXmlForDocumentItems(
              [exams],
              toPost,
              serviceResponse?.entry,
              "ServiceRequest",
            );
            if (auxServiceBasedOn) {
              await PostDocumentReference(
                {
                  ...docReferenceData,
                  base64: examsBaseImage64,
                  codeValue: "SERVICE_REQUEST",
                  codeDisplay: "Solicitação de exame",
                  categoryValue: "service-request",
                  categoryDisplay: "Solicitação",
                },
                references,
                auxServiceBasedOn,
              );
            }
          }
        } catch (err) {
          console.log("Erro ao processar exame de imagem:", err);
        }
      }),
    );

    if (references)
      try {
        const processSimpleMedGroup = async (
          medType: "medication" | "supplement" | "herbalMedicine",
          base64Key: string,
        ) => {
          // Mesmo conjunto do DocumentVisualization / TabsAssinatura pendente
          const pdfGroupItems =
            selectedInterventions?.medicationSimple?.filter(
              (el: any) =>
                el?.type === medType && isPendingSignatureIntervention(el),
            ) || [];

          if (!pdfGroupItems.length) return;

          const toPost = pdfGroupItems.filter(shouldUpsertMedication);
          const needsDocumentReference = pdfGroupItems.some(
            shouldPostInterventionDocument,
          );
          if (!toPost.length && !needsDocumentReference) return;

          const groupBase64 = getDocBase64Payload(readyBase64List, base64Key);

          try {
            let medicationResponse: any;
            if (toPost.length) {
              medicationResponse = await PostMedicationRequest(
                toPost,
                references,
              );
            }

            // DocumentReference do PDF pendente: basedOn = todos do grupo
            // (ids já existentes + locations dos POSTs novos)
            if (needsDocumentReference && groupBase64) {
              const auxBasedOn = buildBasedOnXmlForDocumentItems(
                pdfGroupItems,
                toPost,
                medicationResponse?.entry,
                "MedicationRequest",
              );

              if (auxBasedOn) {
                await PostDocumentReference(
                  {
                    ...docReferenceData,
                    base64: groupBase64,
                    codeValue: "REC_SIM",
                    codeDisplay: "Receituário Simples",
                    categoryValue: "prescricao",
                    categoryDisplay: "Receituário",
                  },
                  references,
                  auxBasedOn,
                );
              } else {
                console.warn(
                  `[${base64Key}] PDF pendente sem basedOn (${pdfGroupItems.length} item(ns))`,
                );
              }
            }
          } catch (err) {
            console.log(`Erro ao processar ${base64Key}:`, err);
          }
        };

        await Promise.allSettled([
          processSimpleMedGroup("medication", "simple-medication"),
          processSimpleMedGroup("supplement", "simple-supplement"),
          processSimpleMedGroup("herbalMedicine", "simple-herbalMedicine"),
        ]);

        // Especiais: 1 PDF / 1 DocumentReference por item pendente
        const specialUnsigned =
          selectedInterventions?.medicationSpecial?.filter(
            (e: any) => isPendingSignatureIntervention(e),
          ) || [];

        if (specialUnsigned.length) {
          await Promise.allSettled(
            specialUnsigned.map(async (special: any, index: number) => {
              if (
                !shouldUpsertMedication(special) &&
                !shouldPostInterventionDocument(special)
              ) {
                return;
              }

              const specialBase64 = getDocBase64Payload(
                readyBase64List,
                `special-${index}`,
              );

              const toPost = shouldUpsertMedication(special) ? [special] : [];
              let specialResponse: any;
              if (toPost.length) {
                specialResponse = await PostMedicationRequest(
                  toPost,
                  references,
                );
              }

              if (
                specialBase64 &&
                shouldPostInterventionDocument(special)
              ) {
                // basedOn: id já existente e/ou location do POST novo
                const auxMedicalBasedOn = buildBasedOnXmlForDocumentItems(
                  [special],
                  toPost,
                  specialResponse?.entry,
                  "MedicationRequest",
                );
                if (auxMedicalBasedOn) {
                  await PostDocumentReference(
                    {
                      ...docReferenceData,
                      base64: specialBase64,
                      codeValue: "REC_ESP",
                      codeDisplay: "Receituário Especial",
                      categoryValue: "prescricao",
                      categoryDisplay: "Receituário",
                    },
                    references,
                    auxMedicalBasedOn,
                  );
                }
              }
            }),
          );
        }

        // Exames gerais (mesmo grupo do PDF "exam")
        const examPdfGroup =
          selectedInterventions?.service?.filter(
            (el: any) =>
              isPendingSignatureIntervention(el) &&
              !["363679005", "108252007"]?.includes(el?.category?.code),
          ) || [];

        if (examPdfGroup.length) {
          const toPost = examPdfGroup.filter(shouldUpsertService);
          const needsDocumentReference = examPdfGroup.some(
            shouldPostInterventionDocument,
          );

          let serviceResponse: any;
          if (toPost.length) {
            serviceResponse = await PostServiceRequest(
              toPost,
              references,
              contained,
            );
          }

          if (needsDocumentReference && serviceBase64) {
            const auxServiceBasedOn = buildBasedOnXmlForDocumentItems(
              examPdfGroup,
              toPost,
              serviceResponse?.entry,
              "ServiceRequest",
            );

            if (auxServiceBasedOn) {
              await PostDocumentReference(
                {
                  ...docReferenceData,
                  base64: serviceBase64,
                  codeValue: "SERVICE_REQUEST",
                  codeDisplay: "Solicitação de exame",
                  categoryValue: "service-request",
                  categoryDisplay: "Solicitação",
                },
                references,
                auxServiceBasedOn,
              );
            }
          }
        }

        // Procedimentos
        const procedurePdfGroup =
          selectedInterventions?.procedure?.filter(
            isPendingSignatureIntervention,
          ) || [];

        if (procedurePdfGroup.length) {
          const toPost = procedurePdfGroup.filter(shouldUpsertService);
          const needsDocumentReference = procedurePdfGroup.some(
            shouldPostInterventionDocument,
          );

          let procedureResponse: any;
          if (toPost.length) {
            procedureResponse = await PostServiceRequest(
              toPost,
              references,
              contained,
            );
          }

          if (needsDocumentReference && procedureBase64) {
            const auxProcedureBasedOn = buildBasedOnXmlForDocumentItems(
              procedurePdfGroup,
              toPost,
              procedureResponse?.entry,
              "ServiceRequest",
            );

            if (auxProcedureBasedOn) {
              await PostDocumentReference(
                {
                  ...docReferenceData,
                  base64: procedureBase64,
                  codeValue: "SERVICE_REQUEST",
                  codeDisplay: "Procedimento",
                  categoryValue: "service-request",
                  categoryDisplay: "Procedimento",
                },
                references,
                auxProcedureBasedOn,
              );
            }
          }
        }

        if (notSignCert?.length) {
          await Promise.allSettled(
            notSignCert.map(async (certificates: any, index: number) => {
              const certificateBase64 = getDocBase64Payload(
                readyBase64List,
                `certificate-${index}`,
              );

              return await PostDocumentReference(
                {
                  ...docReferenceData,
                  base64: certificateBase64,
                  codeValue: "AT",
                  codeDisplay: "Atestado",
                  categoryValue: "atestado",
                  categoryDisplay: "Atestado Médico",
                },
                references,
                null,
              );
            }),
          );
        }
      } catch (err) {
        console.log("err", err);
      }
  };

  const [verifyDialog, setVerifyDialog] = useState<boolean>(false);
  const [related, setRelated] = useState<any>([]);

  async function fetchRelated() {
    const relatedResponse = await GetRelatedPerson({
      patient: `${oids.cpf}-${state?.encounterReference?.subject}`,
    }).then((response) => response?.data?.entry);
    const verifyRelated = relatedResponse?.filter(
      (el: any) =>
        el?.resource?.identifier &&
        !el?.resource?.active &&
        el?.resource?.period?.start,
    );
    if (verifyRelated?.length) setRelated(verifyRelated);
    else setRelated([]);
    return verifyRelated;
  }

  const { summaryData } = useMedicalCareContext();
  const observationsToPatch = useFhirData
    .extractPatientSummaryResource(summaryData, "Observation")?.[0]
    ?.entry?.filter(
      (observation: any) =>
        observation?.resource?.encounter?.reference?.includes(
          state?.encounterReference?.id,
        ) && observation?.resource?.code?.coding?.[0]?.code !== "11369-6",
    );

  const carePlanToPatch = useFhirData
    .extractPatientSummaryResource(summaryData, "CarePlan")?.[0]
    ?.entry?.filter((carePlan: any) =>
      carePlan?.resource?.encounter?.reference?.includes(
        state?.encounterReference?.id,
      ),
    );

  const conditionsToPut = useFhirData
    .extractPatientSummaryResource(summaryData, "Condition")?.[0]
    ?.entry?.filter((condition: any) => {
      const isMatchingEncounter =
        condition?.resource?.encounter?.reference.includes(
          state?.encounterReference?.id,
        );

      const isProblemListItem =
        condition?.resource?.category?.[0]?.coding?.[0]?.code ===
        "problem-list-item";

      return isMatchingEncounter && isProblemListItem;
    });

  const onSubmitAttendance = async (formData: any) => {
    const verifyRelated = await fetchRelated();

    if (verifyRelated?.length && !verifyDialog) {
      setVerifyDialog(true);
      return;
    }

    // Questionários na lista de intervenções não exigem assinatura digital de documento.
    if (
      !warningSignature &&
      selectedInterventions?.all.length &&
      selectedInterventions?.all.filter(
        (el: any) => el?.type !== "questionnaire" && !el.sign,
      )?.length
    ) {
      setActiveStep(2);
      setWarningSignature(true);
      return;
    }

    setLoading(true);

    /**
     * Gravações best-effort do atendimento (antropometria, anamnese, planos,
     * achados físicos e problemas CIAP2). Continuam sendo disparadas na mesma
     * ordem e com os mesmos payloads, mas agora são aguardadas antes do Patch
     * de status e da navegação — desmontar a tela abortava requisições em voo.
     * Falha parcial continua não bloqueando a finalização.
     */
    const pendingWrites: Promise<any>[] = [];
    const failedWrites: string[] = [];

    if (observationsToPatch?.length) {
      observationsToPatch?.map(async (observation: any) => {
        try {
          await PatchStatusObservation({
            observationId: observation?.resource?.id,
            status: "entered-in-error",
          });
        } catch (err) {
          console.log("err", err);
        }
      });
    }

    if (carePlanToPatch?.length) {
      carePlanToPatch?.map(async (carePlan: any) => {
        try {
          await PatchCarePlanStatus({
            carePlanId: carePlan?.resource?.id,
            status: "entered-in-error",
          });
        } catch (err) {
          console.log("err", err);
        }
      });
    }

    if (conditionsToPut?.length) {
      conditionsToPut?.map(async (condition: any) => {
        try {
          await PutCondition(
            {
              ...condition?.resource,
              verificationStatus: {
                coding: [
                  {
                    code: "entered-in-error",
                  },
                ],
              },
            },
            condition?.resource?.id,
          );
        } catch (err) {
          console.log("err", err);
        }
      });
    }

    if (formData?.Anthropometric) {
      const cleanAnthropometrics = Object.keys(formData?.Anthropometric).filter(
        (key) => {
          const value = formData?.Anthropometric?.[key];
          if (key === "pressure") {
            const { sistolic, diastolic } = value;
            return (
              sistolic !== undefined &&
              sistolic !== "" &&
              diastolic !== undefined &&
              diastolic !== ""
            );
          }
          if (
            key === "smoking" ||
            key === "breastfeeding" ||
            key === "exerciseDaysPerWeek" ||
            key === "usualActivityIntensityPhenX" ||
            key === "drugFrequencyPast12Months"
          ) {
            if (value) {
              const { value: fieldValue } = value;
              return fieldValue !== "" && fieldValue !== undefined;
            }
            return false;
          }
          if (
            key === "alcoholicType" ||
            key === "drugTypeLifetime" ||
            key === "physicalActivityTypePhenX"
          ) {
            return Array.isArray(value) && value.length > 0;
          }
          if (key === "exerciseMinutesPerDay") {
            return (
              value !== undefined &&
              value !== "" &&
              value !== null &&
              !isNaN(Number(value))
            );
          }
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          return (
            value !== undefined &&
            value !== "" &&
            value !== " " &&
            value !== null
          );
        },
      );

      const anthropometricWrites = cleanAnthropometrics?.map(async (el: any) => {
        try {
          if (el === "alcoholicType") {
            const selectedAlcoholTypes =
              formData?.Anthropometric?.alcoholicType;
            const alcoholicDailyValue = formData?.Anthropometric?.alcoholic;
            const alcoholicTypeVolumes =
              formData?.Anthropometric?.alcoholicTypeVolumes || {};

            if (
              selectedAlcoholTypes &&
              Array.isArray(selectedAlcoholTypes) &&
              selectedAlcoholTypes.length
            ) {
              await Promise.all(
                selectedAlcoholTypes.map(async (selection: any) => {
                  if (!selection?.observationType) return;

                  const specificVolume = alcoholicTypeVolumes[selection.value];
                  const volumeToUse =
                    specificVolume !== undefined &&
                    specificVolume !== null &&
                    specificVolume !== ""
                      ? specificVolume
                      : alcoholicDailyValue;

                  if (
                    volumeToUse === undefined ||
                    volumeToUse === null ||
                    volumeToUse === ""
                  ) {
                    return;
                  }

                  await PostObservation(
                    {
                      ...formData.Anthropometric,
                      encounterID: references?.id,
                      patientID: references?.subject,
                      practitionerID: user?.username,
                      practitionerName: user?.name,
                      alcoholicType: selection,
                      alcoholic: volumeToUse, // Passa o volume específico ou genérico
                    },
                    selection.observationType,
                  );
                }),
              );
            }

            return;
          }

          if (el === "drugTypeLifetime") {
            const selectedDrugTypes =
              formData?.Anthropometric?.drugTypeLifetime;

            if (
              selectedDrugTypes &&
              Array.isArray(selectedDrugTypes) &&
              selectedDrugTypes.length
            ) {
              await Promise.all(
                selectedDrugTypes.map(async (drugType: any) => {
                  if (!drugType?.code || drugType?.code === "") return;

                  await PostObservation(
                    {
                      ...formData.Anthropometric,
                      encounterID: references?.id,
                      patientID: references?.subject,
                      practitionerID: user?.username,
                      practitionerName: user?.name,
                      drugTypeLifetime: drugType,
                    },
                    "drugTypeLifetime",
                  );
                }),
              );
            }

            return;
          }

          if (el === "physicalActivityTypePhenX") {
            const selectedActivityTypes =
              formData?.Anthropometric?.physicalActivityTypePhenX;

            if (
              selectedActivityTypes &&
              Array.isArray(selectedActivityTypes) &&
              selectedActivityTypes.length
            ) {
              await Promise.all(
                selectedActivityTypes.map(async (activityType: any) => {
                  if (!activityType?.code || activityType?.code === "") return;

                  await PostObservation(
                    {
                      ...formData.Anthropometric,
                      encounterID: references?.id,
                      patientID: references?.subject,
                      practitionerID: user?.username,
                      practitionerName: user?.name,
                      physicalActivityTypePhenX: activityType,
                    },
                    "physicalActivityTypePhenX",
                  );
                }),
              );
            }

            return;
          }

          await PostObservation(
            {
              ...formData.Anthropometric,
              encounterID: references?.id,
              patientID: references?.subject,
              practitionerID: user?.username,
              practitionerName: user?.name,
            },
            el,
          );
        } catch (err) {
          console.log("err", err);
          failedWrites.push("dados antropométricos");
        }
      });
      pendingWrites.push(...anthropometricWrites);
    }

    if (formData?.anamnese) {
      const cleanAnamnese = Object.keys(formData?.anamnese).filter((key) => {
        const value = formData?.anamnese?.[key];
        return (
          value !== undefined && value !== "" && value !== " " && value !== null
        );
      });
      pendingWrites.push(
        ...cleanAnamnese?.map(async (el: any) => {
          try {
            await PostObservation(
              {
                ...formData?.anamnese,
                encounterID: references?.id,
                patientID: references?.subject,
                practitionerID: user?.username,
                practitionerName: user?.name,
              },
              el,
            );
          } catch (err) {
            console.log("err", err);
            failedWrites.push("anamnese");
          }
        }),
      );
    }

    if (formData?.newPlan) {
      // Filtra as chaves que têm valores válidos
      const cleanNewPlan = Object.keys(formData?.newPlan)?.filter((key) => {
        const value = formData?.newPlan?.[key]?.plano;
        return (
          value !== undefined && value !== "" && value !== " " && value !== null
        );
      });

      // Mapeia as chaves filtradas para obter os valores do plano
      const plansToPost = cleanNewPlan.map(
        (key) => formData.newPlan[key].plano,
      );
      pendingWrites.push(
        ...plansToPost.map(async (plan) => {
          try {
            await PostCarePlan({
              plan,
              patientID: references?.subject,
              encounterID: references?.id,
              practitionerID: user?.username,
              practitionerName: user?.name,
            });
          } catch (err) {
            console.log("err", err);
            failedWrites.push("planos de tratamento");
          }
        }),
      );
    }

    const cleanProblems = [...problems, formData?.freeSoloProblem]?.filter(
      (el: any) => el?.length,
    );

    if (examsList?.length) {
      pendingWrites.push(
        ...examsList?.map(async (el: any) => {
          try {
            await PostPhysicalFindinds(el, references);
          } catch (err) {
            console.log("err", err);
            failedWrites.push("achados de exame físico");
          }
        }),
      );
    }

    if (cleanProblems?.length) {
      pendingWrites.push(
        ...cleanProblems.map(async (problem: any) => {
          try {
            await PostCIAP2Condition(
              {
                text: problem,
                patientID: references?.subject,
                encounterID: references?.id,
                practitionerID: user?.username,
                practitionerName: user?.name,
              },
              references,
            );
          } catch (err) {
            console.log("err", err);
            failedWrites.push("lista de problemas");
          }
        }),
      );
    }

    try {
      await handlePrescription();

      /**
       * Envia ao paciente as mesmas Tasks de questionário que o diálogo da agenda cria
       * (useQuestionnaireTaskSend). Falha não bloqueia o fechamento do atendimento.
       */
      const questionnaireInterventions =
        selectedInterventions?.questionnaires ?? [];
      const canSendQuestionnaireTasks =
        questionnaireInterventions.length > 0 &&
        !!patientReferenceForQuestionnaireTasks &&
        !!organizationId &&
        !!practitionerRoleReference &&
        access?.type === "professional";

      if (canSendQuestionnaireTasks) {
        const byId = new Map<string, (typeof questionnaireInterventions)[0]>();
        for (const item of questionnaireInterventions) {
          const qid =
            typeof item?.questionnaireId === "string"
              ? item.questionnaireId.trim()
              : "";
          if (qid && !byId.has(qid)) byId.set(qid, item);
        }

        const usageOk = (
          u: unknown,
        ): u is QuestionnairePayload["usageType"] =>
          u === "triage" ||
          u === "preConsultation" ||
          u === "postConsultation" ||
          u === "attendance";

        let skippedDraftQuestionnaireCount = 0;
        const questionnairesPayload: QuestionnairePayload[] = [];

        for (const item of byId.values()) {
          const id =
            typeof item?.questionnaireId === "string"
              ? item.questionnaireId.trim()
              : "";
          if (!id) continue;

          const full = questionnaireByIdForClinicalSend.get(id);
          if (!full) {
            console.warn(
              "[Footer] Questionário não encontrado na lista da organização; envio ignorado:",
              id,
            );
            continue;
          }
          if (!isQuestionnaireSelectableForClinicalUse(full)) {
            skippedDraftQuestionnaireCount += 1;
            continue;
          }

          const usageType = usageOk(item?.usageType)
            ? item.usageType
            : usageOk(full.usageType)
              ? full.usageType
              : "postConsultation";

          questionnairesPayload.push({
            id,
            title:
              full.title || item?.title || item?.name || "Questionário",
            description: full.description ?? "",
            usageType,
            questions: [] as QuestionnairePayload["questions"],
          });
        }

        if (skippedDraftQuestionnaireCount > 0) {
          toast.info(
            skippedDraftQuestionnaireCount === 1
              ? "1 questionário em rascunho não foi enviado ao paciente."
              : `${skippedDraftQuestionnaireCount} questionários em rascunho não foram enviados ao paciente.`,
          );
        }

        if (questionnairesPayload.length) {
          try {
            await sendQuestionnaireTasks({
              organizationId,
              practitionerRoleReference,
              patientReference: patientReferenceForQuestionnaireTasks,
              ...(appointmentReferenceForQuestionnaireTasks
                ? {
                    appointmentReference: appointmentReferenceForQuestionnaireTasks,
                  }
                : {}),
              questionnaires: questionnairesPayload,
            });
            toast.success(
              `${questionnairesPayload.length} questionário(s) enviado(s) ao paciente.`,
            );
          } catch (questionnaireSendErr) {
            console.error(
              "Erro ao enviar questionários (tasks) ao finalizar atendimento:",
              questionnaireSendErr,
            );
            toast.warning(
              "Atendimento salvo, mas alguns questionários não puderam ser enviados ao paciente. Você pode enviá-los pela agenda.",
            );
          }
        }
      }

      const practitioner = useFhirData.findParticipant(
        state?.encounterReference.participant,
        "Practitioner",
      );

      formData.author = {
        healthcare,
        practitioner,
      };

      formData.problemas = cleanProblems;

      const summary = await BundlePatientSummary({
        identifier: state?.encounterReference?.subject,
        entries: [
          "AllergyIntolerance",
          "Condition",
          "Observation",
          "MedicationRequest",
          "ServiceRequest",
          "Immunization",
        ],
      });

      const answers = BuildQuestionnaireAnswers(
        formData,
        summary,
        state?.encounterReference?.id,
      );

      const questionnaireResponse: any = BuildQuestionnaireResponse({
        answer: answers,
        patient: state?.encounterReference?.subject,
        practitioner: user?.username,
        encounter: state?.encounterReference?.id,
        questionnaireRef: endpoints.QUESTIONNARIE_ID,
        status: "completed",
        protocols: appliedProtocol,
      });

      // await saveCustomization();

      const response = questionnaireDefualt?.resource?.id
        ? await PutQuestionnaireResponse(
            {
              ...questionnaireResponse,
              id: questionnaireDefualt?.resource?.id,
              authored: new Date(appointment?.start),
              extension: questionnaireDefualt?.resource?.extension,
            },
            questionnaireDefualt?.resource?.id,
          )
        : await PostQuestionnaireResponse({
            ...questionnaireResponse,
            authored: new Date(appointment?.start),
          });

      // Nenhuma navegação (nem Patch de status) antes das gravações terminarem.
      if (pendingWrites.length) {
        await Promise.allSettled(pendingWrites);
      }

      // Atendimento do teleconsultor (Encounter com partOf): o Appointment e a sala
      // pertencem ao host. Finaliza só o próprio Encounter.
      const isTeleconsultorEncounter = Boolean(
        state?.encounterReference?.partOf,
      );

      if (response?.id && references?.appointmentId) {
        if (!isTeleconsultorEncounter) {
          await PatchAppointmentStatus({
            appointmentId: references?.appointmentId,
            status: "fulfilled",
          });

          await DeleteWhereby(state?.encounterReference?.sessionKey);
        }

        await PatchEncounterStatus({
          encounterId: references?.id,
          status: "completed",
        });

        try {
          await sendLuxorHistoryAsQuestionnaireResponse();
          console.log("Histórico do LuxorMed enviado com sucesso");
        } catch (error) {
          console.error("Erro ao enviar histórico do LuxorMed:", error);
        }

        // Pós-consulta automática (best-effort): não bloqueia finalização do atendimento.
        // Evolução não dispara WhatsApp de pós-consulta (atendimento sem presença).
        // Teleconsultor também não: a pós-consulta é do atendimento do host.
        if (!isEvolutionAppointment(appointment) && !isTeleconsultorEncounter) {
          try {
            const postConsultationTriggerInput =
              buildClinicalAutomationTriggerInputFromFhirResources({
                organizationId,
                practitionerId: userPractitioner?.id,
                requesterReference: practitionerRoleReference,
                appointment,
                patient,
              });
            if (postConsultationTriggerInput) {
              await triggerPostConsultationAutomation(
                postConsultationTriggerInput,
              );
            }
          } catch (automationError) {
            console.error("[automationTrigger] pós-consulta:", automationError);
          }
        }
      }
      if (failedWrites.length) {
        const uniqueFailures = Array.from(new Set(failedWrites));
        toast.warning(
          `Atendimento registrado, mas houve falha ao salvar: ${uniqueFailures.join(
            ", ",
          )}. Confira o prontuário do paciente.`,
        );
      } else {
        toast.success("Atendimento registrado com sucesso!");
      }
      clearRegisterData();
      setInProgress("");
      setLoading(false);
      setIsSidebarExpanded(true);
      navigate("/");
    } catch (err) {
      console.log("err", err);
      setLoading(false);
    }
  };
  /**
   * Guarda de reentrância: `setLoading(true)` só acontece depois do
   * `await fetchRelated()` e dos early-returns de diálogo, e existem três
   * gatilhos independentes do mesmo submit (ActionBar, VerifyResponsibleDialog e
   * WarningDocumentsDialog). Nessa janela, dois cliques geravam
   * QuestionnaireResponse e Condition duplicados.
   */
  const onSubmit = async (formData: any) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      await onSubmitAttendance(formData);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // Registra a finalização no contexto para que o botão movido ao StepArea
  // possa acioná-la, mantendo toda a lógica de submit aqui no Footer.
  finalizeAttendanceRef.current = handleSubmit(onSubmit);

  // Sincroniza o loading local com o contexto (spinner do botão no StepArea).
  useEffect(() => {
    setIsFinalizingAttendance(loading);
  }, [loading, setIsFinalizingAttendance]);

  return (
    <>
      <Stack
        height="56px"
        width="100%"
        direction="row"
        alignItems="center"
        spacing={2}
        px={2}
      >
        <ShortCutsViewer />
        <Divider orientation="vertical" flexItem />

        <Stack direction="row" spacing={1} width="100%" justifyContent="center">
          {activeStep !== 2 && registerAreaTab !== 1 && (
            <>
              <Fab
                size="small"
                variant="extended"
                onClick={() => {
                  setOpenDialog(true);
                  setType(0);
                }}
              >
                <Add sx={{ mr: 0.5 }} />
                Diagnóstico
              </Fab>

              <Fab
                size="small"
                variant="extended"
                onClick={() => {
                  setOpenDialog(true);
                  setType(1);
                }}
              >
                <Add sx={{ mr: 0.5 }} />
                Conduta
              </Fab>
              <Fab
                size="small"
                variant="extended"
                onClick={() => {
                  setOpenDialog(true);
                  setType(2);
                }}
              >
                <Add sx={{ mr: 0.5 }} />
                Problema
              </Fab>
            </>
          )}
        </Stack>
        {/* O botão "Voltar" foi movido para a esquerda do StepArea (somente ícone).
            Continuar/Finalizar estão na ActionBar. */}
      </Stack>

      {openDialog && (
        <QuickActionsDialog
          control={control}
          isOpen={openDialog}
          handleClose={() => {
            setOpenDialog(false);
          }}
          type={type}
        />
      )}

      {verifyDialog && (
        <VerifyResponsibleDialog
          submit={handleSubmit(onSubmit)}
          handleClose={() => setVerifyDialog(false)}
          isOpen={true}
          loading={loading}
          setLoading={setLoading}
          dataAppointment={appointment}
          related={related}
          fetchRelated={fetchRelated}
          patientId={state?.encounterReference?.subject}
        />
      )}

      {warningSignature && (
        <WarningDocumentsDialog
          isOpen={warningSignature}
          onClose={() => setWarningSignature(false)}
          submit={handleSubmit(onSubmit)}
          loading={loading}
        />
      )}
    </>
  );
}
